import { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useQuery } from '@tanstack/react-query';

import { fetchSalesOrderSnCandidates } from '#/api/crmMgmt/salesOrder/fetchSnCandidates';
import SalesOrderSnLineMtrTabs from '#/modules/crmMgmt/salesOrder/components/SalesOrderSnLineMtrTabs';
import { getSalesOrderApiErrorMessage } from '#/modules/crmMgmt/salesOrder/helper/apiError';
import { canEditSalesOrderDraft } from '#/modules/crmMgmt/salesOrder/helper/salesOrderStatus';
import { buildSalesOrderSnSelectionMap } from '#/modules/crmMgmt/salesOrder/helper/salesOrderSnSelection';
import { salesOrderSnCandidatesQueryKey } from '#/utils/queryKeys';

function renderSelectedSnList(mtr, selectedIds) {
  if (selectedIds.length === 0) {
    return <p className="py-4 text-sm text-muted-foreground">尚未選擇序號</p>;
  }

  const serialById = new Map(
    (mtr?.candidates ?? []).map((candidate) => [String(candidate.mtrSnId), candidate.serialNumber]),
  );

  return (
    <ul className="divide-y divide-slate-200 rounded-md border border-slate-200 bg-panel">
      {selectedIds.map((snId) => (
        <li key={snId} className="px-3 py-2 font-mono text-sm">
          {serialById.get(snId) ?? snId}
        </li>
      ))}
    </ul>
  );
}

/**
 * 銷貨單詳情「選擇序號」分頁（唯讀）：產品 → 物料 → 已選 SN。
 * 勾選／儲存在獨立頁 `SalesOrderSnSelectPage`。sn-candidates 僅 DRAFT。
 */
const SalesOrderSnSelectionSection = ({ detail }) => {
  const salesOrderId = detail?.id ? String(detail.id) : '';
  const isDraft = canEditSalesOrderDraft(detail?.status);

  const candidatesQuery = useQuery({
    queryKey: salesOrderSnCandidatesQueryKey(salesOrderId),
    queryFn: () => fetchSalesOrderSnCandidates(salesOrderId),
    enabled: Boolean(salesOrderId) && isDraft,
  });

  const snLines = candidatesQuery.data?.data?.lines;
  const selectionMap = useMemo(() => buildSalesOrderSnSelectionMap(snLines), [snLines]);

  if (!isDraft) {
    return <p className="px-4 py-5 text-sm text-muted-foreground">僅草稿可查看與選擇序號。</p>;
  }

  if (candidatesQuery.isLoading) {
    return <p className="px-4 py-5 text-sm text-slate-500">載入中…</p>;
  }

  if (candidatesQuery.isError) {
    return (
      <p className="px-4 py-5 text-sm text-destructive">
        {getSalesOrderApiErrorMessage(candidatesQuery.error, '載入序號候選失敗')}
      </p>
    );
  }

  return (
    <div className="px-4 py-4">
      <p className="mb-3 text-xs text-muted-foreground">各物料已選序號；送審時才占用。</p>
      <SalesOrderSnLineMtrTabs
        detailLines={detail?.lines ?? []}
        snLines={snLines ?? []}
        selectionMap={selectionMap}
        renderMtrContent={renderSelectedSnList}
      />
    </div>
  );
};

SalesOrderSnSelectionSection.propTypes = {
  detail: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    status: PropTypes.string,
    lines: PropTypes.arrayOf(PropTypes.object),
  }).isRequired,
};

export default SalesOrderSnSelectionSection;
